import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";
import userApi from "../api/user";
import { User, UserWithProfiles } from "../types/user";
import { formatUserName } from "../utils/formats";

export type UserState = {
  isLoaded: boolean,
  user: null | UserWithProfiles,
}

/*  user | isLoaded |
 *   yes |   yes    | user is present
 *   yes |   no     | user is present but can disappear
 *   no  |   yes    | user isn't present and authentication needed
 *   no  |   no     | user isn't present but can appear
 */

const userWithProfiles = (user: User): UserWithProfiles => {
  const currentCompany = user.companies?.find(c => c.id === user.currentProfileId);
  const userName = formatUserName(user);
  return {
    ...user,
    currentProfileConfirmed: !!(currentCompany ? currentCompany.confirmedAt : user.confirmedAt),
    currentProfileId: user.currentProfileId || 0,
    currentProfileInvestmentStatus: currentCompany ? currentCompany.investmentStatus : user.investmentStatus,
    currentProfileName: currentCompany ? currentCompany.name : userName,
    currentCompanyOnboardingStep: currentCompany ? currentCompany.companyOnboardingStep : null,
    mzid: currentCompany ? currentCompany.mzid : user.mzid,
    profiles: [
      { confirmedAt: user.confirmedAt, id: 0, name: userName, investmentStatus: user.investmentStatus },
      ...(user.companies || [])
    ],
  }
}

const initialState = {
  isLoaded: false,
  user: App.user
    ? {
        ...App.user,
        currentProfileId: 0,
        currentProfileInvestmentStatus: null,
        currentProfileName: formatUserName(App.user),
        currentCompanyOnboardingStep: null,
        profiles: [],
        referralCode: "",
        investorOnboardingStep: null
      }
    : null,
}

export const UserContext = createContext<UserState>(initialState);

interface Props {
  children?: ReactNode;
}

export const UserContextProvider = ({ children }: Props): JSX.Element => {
  const [userState, setUserState] = useState<UserState>(initialState);

  useEffect(() => {
    void userApi.getCurrentUser()
      .then((user) => setUserState({ isLoaded: true, user: userWithProfiles(user) }))
      .catch(() => setUserState({ isLoaded: true, user: null }));
  }, [setUserState]);

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
}

export const useUserContext = () => useContext(UserContext);
